'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/components/AuthProvider'
import { isLabPerformanceAgent } from '@/lib/defer-third-party'
import { showRewardedAd } from '@/lib/gpt-ads'
import { getOfferwallSettings } from '@/lib/offerwall-settings'
import { beginAdSenseOfferwall, isAdSenseOfferwallAttempt } from '@/lib/google-offerwall'
import {
  grantOfferwallAccess,
  hasOfferwallAccess,
  rememberOfferwallArticleAccess,
  resolveOfferwallArticleDecision,
  shouldResetOfferwallAttempt,
  subscribeOfferwallAccess,
} from '@/lib/offerwall-access'
import type { ArticleAccessMode, OfferwallPolicy, OfferwallProvider } from '@/lib/offerwall-access'
import {
  getArticleAccessMode,
  getOfferwallAccessMinutes,
  isOfferwallEnabled,
  resolveRewardedGamAdUnitPath,
} from '@/lib/public-site-config'

type GateStatus = 'checking' | 'unlocked' | 'locked' | 'loading'
type GateSettings = OfferwallPolicy & { signature: string }

const UNAVAILABLE_MESSAGE = 'Reklam şu anda gösterilemiyor. Bu haberi okumaya devam edebilirsiniz.'

/** Only a confirmed reward creates shared, time-limited access. Unavailable ads fail open. */
export default function OfferwallGate({
  children,
  suppressed = false,
}: {
  children: ReactNode
  /** Empty article bodies do not need an access prompt. */
  suppressed?: boolean
}) {
  const { user, isPremium, isLoading: authLoading } = useAuth()
  const pathname = usePathname()
  const isMember = Boolean(user)
  const memberRef = useRef(isMember)
  const [status, setStatus] = useState<GateStatus>('checking')
  const [accessMode, setAccessMode] = useState<ArticleAccessMode>('open')
  const [provider, setProvider] = useState<OfferwallProvider>('gam_rewarded')
  const [accessMinutes, setAccessMinutes] = useState(30)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const headingId = useId()
  const statusRef = useRef<GateStatus>('checking')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const visibleRef = useRef(false)
  const mountedRef = useRef(false)
  const settingsRef = useRef<GateSettings | null>(null)
  const settingsGenerationRef = useRef(0)
  const watchGenerationRef = useRef(0)
  const adAbortRef = useRef<AbortController | null>(null)
  // Expiration applies to the next article; do not close one already being read.
  const earnedForArticleRef = useRef(false)
  const unavailableSignatureRef = useRef('')
  const nativeDismissedRef = useRef(false)

  const updateStatus = useCallback((next: GateStatus) => {
    statusRef.current = next
    setStatus(next)
  }, [])

  const cancelWatch = useCallback(() => {
    watchGenerationRef.current++
    adAbortRef.current?.abort()
    adAbortRef.current = null
  }, [])

  const cancelSettings = useCallback(() => {
    settingsGenerationRef.current++
  }, [])

  const refreshSettings = useCallback(async (force = false) => {
    const generation = ++settingsGenerationRef.current
    if (authLoading || suppressed || isPremium || isLabPerformanceAgent()) {
      cancelWatch()
      updateStatus(authLoading ? 'checking' : 'unlocked')
      setErrorMsg(null)
      setNotice(null)
      return
    }
    try {
      const config = await getOfferwallSettings(force)
      if (!mountedRef.current || generation !== settingsGenerationRef.current) return
      const mode = getArticleAccessMode(config)
      const nextProvider = config.offerwall_provider === 'adsense' ? 'adsense' : 'gam_rewarded'
      const enabled = isOfferwallEnabled(config)
      const path = nextProvider === 'gam_rewarded' ? resolveRewardedGamAdUnitPath(config) : ''
      const minutes = getOfferwallAccessMinutes(config)
      const signature = JSON.stringify([mode, nextProvider, enabled, path])
      const previous = settingsRef.current
      const policy: GateSettings = { mode, provider: nextProvider, enabled, path, minutes, signature }
      const changed = shouldResetOfferwallAttempt(previous, policy)
      settingsRef.current = policy
      setAccessMode(mode)
      setProvider(nextProvider)
      if (statusRef.current !== 'loading' || changed) setAccessMinutes(minutes)
      if (changed) {
        cancelWatch()
        unavailableSignatureRef.current = ''
        setErrorMsg(null)
        setNotice(null)
      }
      const useGamAccess = mode === 'rewarded' && nextProvider === 'gam_rewarded'
      if (useGamAccess) {
        earnedForArticleRef.current = rememberOfferwallArticleAccess(earnedForArticleRef.current, visibleRef.current)
      }
      const decision = resolveOfferwallArticleDecision(policy, {
        isMember: memberRef.current,
        hasRewardAccess: mode === 'rewarded' && hasOfferwallAccess(),
        alreadyRead: earnedForArticleRef.current,
        unavailable: unavailableSignatureRef.current === signature,
        nativeAttempt: nextProvider === 'adsense' && isAdSenseOfferwallAttempt(),
        nativeDismissed: nativeDismissedRef.current,
      })
      if (decision === 'open') {
        updateStatus('unlocked')
        if (!useGamAccess || unavailableSignatureRef.current !== signature) setNotice(null)
      } else if (statusRef.current !== 'loading' || changed) {
        updateStatus('locked')
      }
    } catch {
      // A settings outage must not hide the article or interrupt an active ad.
      if (mountedRef.current && generation === settingsGenerationRef.current && statusRef.current === 'checking') {
        updateStatus('unlocked')
      }
    }
  }, [authLoading, suppressed, isPremium, cancelWatch, updateStatus])

  useEffect(() => {
    memberRef.current = isMember
    if (settingsRef.current?.mode === 'members') updateStatus(isMember ? 'unlocked' : 'locked')
  }, [isMember, updateStatus])

  useEffect(() => {
    mountedRef.current = true
    void refreshSettings()
    const refreshVisible = () => {
      if (document.visibilityState !== 'hidden') void refreshSettings(true)
    }
    const unsubscribe = subscribeOfferwallAccess(() => {
      if (settingsRef.current?.mode !== 'rewarded') return
      if (!hasOfferwallAccess()) return
      if (settingsRef.current.provider === 'gam_rewarded') {
        earnedForArticleRef.current = rememberOfferwallArticleAccess(earnedForArticleRef.current, visibleRef.current)
      }
      cancelWatch()
      updateStatus('unlocked')
      setErrorMsg(null)
      setNotice(null)
    })
    window.addEventListener('focus', refreshVisible)
    document.addEventListener('visibilitychange', refreshVisible)
    const interval = setInterval(refreshVisible, 45_000)
    return () => {
      mountedRef.current = false
      cancelSettings()
      cancelWatch()
      unsubscribe()
      clearInterval(interval)
      window.removeEventListener('focus', refreshVisible)
      document.removeEventListener('visibilitychange', refreshVisible)
    }
  }, [refreshSettings, cancelSettings, cancelWatch, updateStatus])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const updateVisibility = (visible: boolean) => {
      const becameVisible = visible && !visibleRef.current
      visibleRef.current = visible
      if (becameVisible) void refreshSettings()
    }
    const check = () => {
      const bounds = container.getBoundingClientRect()
      updateVisibility(document.visibilityState !== 'hidden' && bounds.bottom > 0 && bounds.top < window.innerHeight && bounds.height > 0)
    }
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (entry) updateVisibility(entry.isIntersecting && document.visibilityState !== 'hidden')
      })
      observer.observe(container)
      document.addEventListener('visibilitychange', check)
      return () => {
        observer.disconnect()
        document.removeEventListener('visibilitychange', check)
      }
    }
    check()
    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check)
    document.addEventListener('visibilitychange', check)
    return () => {
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
      document.removeEventListener('visibilitychange', check)
    }
  }, [refreshSettings])

  const watchAd = useCallback(async () => {
    if (statusRef.current === 'loading') return
    if (authLoading || suppressed || isPremium) {
      updateStatus('unlocked')
      setErrorMsg(null)
      return
    }
    const settings = settingsRef.current
    if (!settings?.enabled || settings.mode !== 'rewarded') {
      void refreshSettings(true)
      return
    }
    if (hasOfferwallAccess()) {
      if (settings.provider === 'gam_rewarded') {
        earnedForArticleRef.current = rememberOfferwallArticleAccess(earnedForArticleRef.current, visibleRef.current)
      }
      updateStatus('unlocked')
      setErrorMsg(null)
      return
    }
    if (settings.provider === 'adsense') {
      setErrorMsg(null)
      if (beginAdSenseOfferwall()) {
        nativeDismissedRef.current = true
        updateStatus('unlocked')
      } else {
        setErrorMsg('Google reklam ekranı açılamadı. Haberi okumaya devam edebilirsiniz.')
      }
      return
    }
    if (!settings.path) {
      void refreshSettings(true)
      return
    }
    cancelWatch()
    const generation = watchGenerationRef.current
    const controller = new AbortController()
    adAbortRef.current = controller
    const current = () => mountedRef.current && generation === watchGenerationRef.current && !controller.signal.aborted
    let diagnostic = ''
    setErrorMsg(null)
    setNotice(null)
    updateStatus('loading')
    try {
      const result = await showRewardedAd(settings.path, 20_000, {
        signal: controller.signal,
        onDiagnostic: (reason) => { diagnostic = reason },
      })
      if (!current()) return
      adAbortRef.current = null
      if (result === 'granted') {
        earnedForArticleRef.current = true
        grantOfferwallAccess(settings.minutes)
        updateStatus('unlocked')
      } else if (result === 'closed') {
        setErrorMsg(diagnostic === 'busy'
          ? 'Başka bir reklam açık. Tamamlandıktan sonra yeniden deneyin.'
          : 'Reklam tamamlanmadı. İçeriğe erişmek için reklamı sonuna kadar izleyin.')
        updateStatus('locked')
      } else {
        unavailableSignatureRef.current = settings.signature
        setNotice(UNAVAILABLE_MESSAGE)
        updateStatus('unlocked')
      }
    } catch {
      if (!current()) return
      unavailableSignatureRef.current = settings.signature
      setNotice(UNAVAILABLE_MESSAGE)
      updateStatus('unlocked')
    } finally {
      if (adAbortRef.current === controller) adAbortRef.current = null
      if (mountedRef.current && generation === watchGenerationRef.current && settingsRef.current) {
        setAccessMinutes(settingsRef.current.minutes)
      }
    }
  }, [authLoading, suppressed, isPremium, cancelWatch, refreshSettings, updateStatus])

  const loading = status === 'loading'
  const membersOnly = accessMode === 'members'
  const showCard = (membersOnly ? !isMember : status === 'locked' || loading) && !authLoading && !isPremium && !suppressed
  const nativeSupport = accessMode === 'rewarded' && provider === 'adsense'
  const gated = showCard && !nativeSupport
  const accessLabel = accessMinutes % 60 === 0 ? `${accessMinutes / 60} saat` : `${accessMinutes} dakika`
  const redirect = encodeURIComponent(pathname || '/')

  // Preserve the content DOM across state changes: embed/lightbox hooks retain their root.
  // SSR, authentication and initial settings checks apply no clipping or invisible lock.
  return (
    <div ref={containerRef} className={showCard ? 'mb-8 min-w-0 max-w-full' : undefined}>
      <div className={gated ? 'relative max-h-64 select-none overflow-hidden' : undefined} aria-hidden={gated || undefined} inert={gated || undefined}>
        {children}
        {gated && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent dark:from-slate-950" />}
      </div>
      {showCard && <section aria-labelledby={headingId} aria-busy={loading} className="mx-auto mt-3 w-full max-w-md rounded-2xl border border-gray-200 bg-white p-4 text-center shadow-lg sm:p-6 dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto mb-3 flex size-11 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400">
          <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden>
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.75 10.5l-4.5-2.6a.5.5 0 00-.75.44v5.32a.5.5 0 00.75.43l4.5-2.59a.5.5 0 000-.87zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 id={headingId} className="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">
          {membersOnly ? 'Haberin devamını okumak için giriş yapın' : 'Haberciliğimize destek olun'}
        </h2>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-gray-600 dark:text-slate-400">
          {membersOnly
            ? 'Bu içeriğin tamamına erişmek için giriş yapabilir veya ücretsiz üye olabilirsiniz.'
            : 'Reklam izleyerek ücretsiz ve güncel haberciliğimize destek olabilirsiniz.'}
        </p>
        {!membersOnly && !nativeSupport && <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">Reklamı tamamladığınızda {accessLabel} boyunca tekrar sorulmaz.</p>}
        {nativeSupport && <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">Seçtiğinizde Google reklam ekranı açılır. Reklam ve erişim koşullarını Google belirler.</p>}
        {errorMsg && <p role="alert" className="mx-auto mt-2 max-w-sm text-sm font-medium text-red-600 dark:text-red-400">{errorMsg}</p>}
        {membersOnly ? <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <Link href={`/login?redirect=${redirect}`} className="inline-flex min-h-12 flex-1 items-center justify-center rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-700">Giriş Yap</Link>
          <Link href={`/register?redirect=${redirect}`} className="inline-flex min-h-12 flex-1 items-center justify-center rounded-xl border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-800 dark:border-slate-600 dark:text-slate-200">Üye Ol</Link>
        </div> : <div className="mt-5">
          <button type="button" onClick={watchAd} disabled={loading} aria-label={loading ? 'Reklam yükleniyor…' : 'Reklam İzle ve Destek Ol'} className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-3 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-wait disabled:opacity-70 sm:px-6 sm:text-base">
            {loading && <svg viewBox="0 0 24 24" fill="none" className="size-4 shrink-0 animate-spin" aria-hidden>
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
              <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeLinecap="round" strokeWidth="3" />
            </svg>}
            <span role="status" aria-live="polite" style={{ color: 'inherit' }}>{loading ? 'Reklam yükleniyor…' : 'Reklam İzle ve Destek Ol'}</span>
          </button>
          {nativeSupport && <button type="button" onClick={() => { nativeDismissedRef.current = true; setErrorMsg(null); updateStatus('unlocked') }} className="mt-2 min-h-10 rounded-lg px-3 text-xs text-gray-500 underline dark:text-slate-400">Şimdi değil</button>}
        </div>}
      </section>}
      {!gated && notice && !isPremium && !suppressed && <p role="status" className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-slate-400">{notice}</p>}
    </div>
  )
}
